import {
  ChangeViewIcon,
  DeleteIcon,
  FullscreenActiveIcon,
  FullscreenIcon,
  LockIcon,
  TimetableIcon,
  UnlockIcon,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useContainerDimensions } from '../../../hooks/useDimensions';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { MenuBar, MenuBarEntry, Subaction } from '../../MenuBar/MenuBar';
import { Slider } from '../../Slider/Slider';
import { useTeachingBlockConflicts } from '../Contexts/TeachingBlockConflictContext';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { GridContext, GridHighlightType } from '../types';
import styles from './GridMenuBar.module.scss';

export const GridMenuBar: FC = observer(() => {
  const { t } = useTranslation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width } = useContainerDimensions(wrapperRef);
  const isCompact = width < 1220;

  const store = useTeachingBlockStore();
  const userConfig = useUserConfigContext();

  const { calculateConflicts, toggleCalculateConflicts } = useTeachingBlockConflicts();

  const allLocked =
    store.placedCards.length > 0 && store.placedCards.filter((card) => card.locked).length === store.placedCards.length;

  const isFullscreen = store.isFullscreen;

  function onFullscreen() {
    const grid = document.querySelector('#teaching-block-grid');
    if (!grid) return;
    if (!document.fullscreenElement) {
      grid.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
      });
    } else {
      void document.exitFullscreen();
    }
  }

  function onChangeView(value: GridContext) {
    // Bewusste Auswahl: selectContext speichert sie in der UserConfig.
    store.selectContext(value);
  }

  function onChangeHighlightMode(highlight: GridHighlightType) {
    store.setHighlightMode(highlight);
  }

  useEffect(() => {
    const fullscreenchanged = () => {
      const active = document.fullscreenElement !== null;
      store.setIsFullscreen(active);
      document.body.classList.toggle('fullscreen', active);
    };
    document.addEventListener('fullscreenchange', fullscreenchanged);
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenchanged);
    };
  }, [store]);

  const { ConfirmationDialog: DeleteDioalog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('pinboard.removeAllConfirm'),
    defaultConfirmText: t('pinboard.actions.removeAll'),
  });

  const { ConfirmationDialog: LockDialog, confirm: confirmLock } = useConfirm({
    defaultTitle: allLocked ? t('pinboard.unlockAllConfirm') : t('pinboard.lockAllConfirm'),
    defaultConfirmText: allLocked ? t('pinboard.actions.unlockAll') : t('pinboard.actions.lockAll'),
  });

  const toggleAllActions: MenuBarEntry[] = [
    {
      actions: [
        {
          icon: allLocked ? <UnlockIcon /> : <LockIcon />,
          title: allLocked ? t('pinboard.actions.unlockAll') : t('pinboard.actions.lockAll'),
          action: async () => {
            const res = await confirmLock();
            if (res) {
              allLocked ? store.unlockAllCards() : store.lockAllCards();
            }
          },
        },
        {
          icon: <DeleteIcon />,
          title: t('pinboard.actions.removeAll'),
          action: async () => {
            const res = await confirmDelete();
            if (res) {
              store.discardAllCards();
            }
          },
        },
      ],
    },
  ];

  const noop = () => undefined;
  const ruler: Subaction = { title: null, action: null, type: 'ruler' };
  const rowHeightNode: Subaction = {
    type: 'component',
    node: (
      <>
        <div className={'mr-2'} onClick={() => store.setDefaultRowHeight()}>
          {t('pinboard.actions.rowHeightLabel')}
        </div>
        <Slider
          value={[store.rowHeight]}
          min={30}
          max={75}
          step={1}
          onValueChange={(value) => store.setRowHeight(value[0])}
        />
      </>
    ),
  };
  const showAllRowsItem: Subaction = {
    title: t('pinboard.actions.showAllRows', { count: store.hiddenRows.size }),
    action: () => store.showAllRows(),
  };

  const heading = (title: string): Subaction => ({ type: 'heading', title });
  const toggle = (title: string, action: () => void, value: boolean, disabled?: boolean): Subaction => ({
    title,
    action,
    type: 'switch',
    value,
    disabled,
  });

  // "Ansicht" = was auf dem Board steht: zuerst der Modus (exklusive Auswahl), darunter die
  // Schalter, die auf diesem Modus liegen — nach Wirkung gruppiert, wie in der Stundenplan-Stecktafel.
  const viewSubs: Subaction[] = [
    { title: t('pinboard.actions.viewClass'), action: () => onChangeView('classes') },
    { title: t('pinboard.actions.viewTeacher'), action: () => onChangeView('teachers') },
    { title: t('pinboard.actions.viewRoom'), action: () => onChangeView('rooms') },
    ruler,
    heading(t('pinboard.actions.sectionHighlights')),
    toggle(t('pinboard.actions.disableConflicts'), () => toggleCalculateConflicts(), calculateConflicts, true),
    toggle(
      t('pinboard.actions.showWarnings'),
      () => onChangeHighlightMode('warnings'),
      store.highlightMode.includes('warnings'),
    ),
    toggle(
      t('pinboard.actions.showNoRooms'),
      () => onChangeHighlightMode('rooms'),
      store.highlightMode.includes('rooms'),
    ),
    toggle(
      t('pinboard.actions.showLengthDeviation'),
      () => onChangeHighlightMode('lengthDeviation'),
      store.highlightMode.includes('lengthDeviation'),
    ),
    ruler,
    heading(t('pinboard.actions.sectionCards')),
    toggle(
      t('pinboard.actions.showSubjectColor'),
      () => store.setCardColorBySubject(!store.cardColorBySubject),
      store.cardColorBySubject,
    ),
    toggle(
      t('pinboard.actions.showTeacherInsteadOfSubject'),
      () => store.setShowTeacherInsteadOfSubject(!store.showTeacherInsteadOfSubject),
      store.showTeacherInsteadOfSubject,
    ),
    toggle(
      t('pinboard.actions.hideIcons'),
      () => onChangeHighlightMode('noIcons'),
      store.highlightMode.includes('noIcons'),
    ),
    ruler,
    heading(t('pinboard.actions.sectionFilter')),
    // Betrifft nur die Zeilenachse der Raum-Ansicht.
    toggle(
      t('pinboard.actions.groupRoomsByBuilding'),
      () => store.setGroupRoomsByBuilding(!store.groupRoomsByBuilding),
      store.groupRoomsByBuilding,
      store.gridContext !== 'rooms',
    ),
  ];

  // "Layout" = wie die Arbeitsfläche aufgebaut ist (Panels, Raster) — unabhängig vom Planinhalt.
  const layoutSubs: Subaction[] = [
    toggle(t('pinboard.actions.sideBySideLayout'), () => store.toggleBoardLayout(), store.boardLayout === 'split'),
    toggle(t('pinboard.actions.showPreviewMap'), () => store.toggleShowPreviewMap(), store.showPreviewMap),
    ruler,
    rowHeightNode,
    showAllRowsItem,
    ruler,
    {
      // Setzt die gespeicherten Ansichtseinstellungen dieses Boards zurueck; die
      // offene Ansicht uebernimmt das sofort ueber die Observables.
      title: t('pinboard.actions.resetView'),
      action: () => void userConfig.resetSettings(['teachingBlock']),
    },
  ];

  const fullscreenGroup: MenuBarEntry = {
    actions: [
      {
        icon: isFullscreen ? <FullscreenActiveIcon /> : <FullscreenIcon />,
        title: isFullscreen ? t('common.fullscreenCancel') : t('common.fullscreen'),
        action: () => onFullscreen(),
      },
    ],
  };

  // Ein gemeinsames "Anzeige ändern"-Dropdown (wenig Platz / Sondermodi).
  const singleViewGroup: MenuBarEntry = {
    actions: [
      {
        icon: <ChangeViewIcon />,
        title: t('pinboard.actions.changeView'),
        action: noop,
        subactions: [...viewSubs, ruler, heading(t('pinboard.actions.sectionLayout')), ...layoutSubs],
      },
    ],
  };

  // Ein Dropdown je Abschnitt (genug Platz).
  const sectionViewGroup: MenuBarEntry = {
    actions: [
      { icon: <ChangeViewIcon />, title: t('pinboard.actions.sectionView'), action: noop, subactions: viewSubs },
      { icon: <TimetableIcon />, title: t('pinboard.actions.sectionLayout'), action: noop, subactions: layoutSubs },
    ],
  };

  const toggleViewActions: MenuBarEntry[] = isCompact
    ? [singleViewGroup, fullscreenGroup]
    : [sectionViewGroup, fullscreenGroup];

  const compactEntries: MenuBarEntry[] = [
    // {
    //   actions: [
    //     {
    //       icon: <TimetableIcon />,
    //       title: t('common.additionalActions'),
    //       action: () => {},
    //       subactions: [
    //         {
    //           title: t('pinboard.actions.upload'),
    //           action: () => upload(),
    //         },
    //         {
    //           title: t('pinboard.actions.download'),
    //           action: () => download(),
    //         },
    //         {
    //           title: t('pinboard.actions.improve'),
    //           action: () => console.log('improve clicked'),
    //           disabled: true,
    //         },
    //         {
    //           title: t('pinboard.actions.assignRoom'),
    //           action: () => console.log('room clicked'),
    //           disabled: true,
    //         },
    //         {
    //           title: t('pinboard.actions.test'),
    //           action: () => test(),
    //         },
    //         {
    //           title: t('pinboard.actions.control'),
    //           action: () => control(),
    //         },
    //       ],
    //     },
    //   ],
    // },
    ...toggleAllActions,
    ...toggleViewActions,
  ];

  const entries: MenuBarEntry[] = [
    // {
    //   actions: [
    //     {
    //       icon: <GenerateIcon />,
    //       title: t('pinboard.actions.generate'),
    //       action: () => console.log('generate clicked'),
    //       subactions: [
    //         {
    //           title: t('pinboard.actions.upload'),
    //           action: () => upload(),
    //         },
    //         {
    //           title: t('pinboard.actions.download'),
    //           action: () => download(),
    //         },
    //       ],
    //     },
    //     {
    //       icon: <ImproveIcon />,
    //       title: t('pinboard.actions.improve'),
    //       action: () => console.log('improve clicked'),
    //       disabled: true,
    //     },
    //   ],
    // },
    // {
    //   actions: [
    //     {
    //       icon: <RoomIcon />,
    //       title: t('pinboard.actions.assignRoom'),
    //       action: () => console.log('room clicked'),
    //       disabled: true,
    //     },
    //     {
    //       icon: <TestIcon />,
    //       title: t('pinboard.actions.test'),
    //       action: () => test(),
    //     },
    //     {
    //       icon: <ControlIcon />,
    //       title: t('pinboard.actions.control'),
    //       action: () => control(),
    //     },
    //   ],
    // },
    ...toggleAllActions,
    ...toggleViewActions,
  ];

  // const controlModeEntries: MenuBarEntry[] = [
  //   {
  //     actions: [
  //       {
  //         icon: <ControlIcon />,
  //         title: t('pinboard.control.quit'),
  //         action: () => control(),
  //       },
  //     ],
  //   },
  //   {
  //     actions: [
  //       {
  //         icon: <ArrowDownIcon />,
  //         title: t('pinboard.control.expandAll'),
  //         action: () => pinboardStore.expandControlResults(true),
  //       },
  //       {
  //         icon: <ArrowUpIcon />,
  //         title: t('pinboard.control.collapseAll'),
  //         action: () => pinboardStore.expandControlResults(false),
  //       },
  //     ],
  //   },
  //   ...toggleViewActions,
  // ];

  return (
    <div className={styles['grid-menu-bar']} ref={wrapperRef}>
      <MenuBar
        entries={isCompact ? compactEntries : entries}
        usePortal={!isFullscreen}
        // passive={pinboardStore.controlMode.isActive}
      />
      <LockDialog />
      <DeleteDioalog />
    </div>
  );
});
